import {
  Box,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  useMenuContext,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useWorkspaceSwitcher } from '../../../hooks/workspace/useWorkspaceSwitcher';
import { useIsReadOnly } from '../../../hooks/user/useWorkspaceRole';
import { useCanUseFeature } from '../../../hooks/ui/useFeatureGate';
import { UnsavedWorkDialog } from '../../dialogs/UnsavedWorkDialog';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { MENU_ITEM_GLYPH_SIZE } from '../../ui/iconButtonMetrics';
import { WorkspaceLogo } from '../../workspace/WorkspaceLogo';
import { ROUTES } from '../../../constants/routes';
import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { safeAnalytics } from '../../../utils/analytics';
import { NAV_RAIL_TARGET_SIZE } from './navRailWidth';

/**
 * The rail's trigger: Create's square, logo edge to edge. Hover dims the logo
 * rather than drawing a box round it. Open and keyboard focus draw the ring
 * INSIDE the tile, flush with its edge, so it stays Create's size; on a
 * pseudo-element, because an inset box-shadow paints under the logo, which is
 * opaque and fills the button.
 */
const RAIL_TRIGGER_STYLE = {
  boxSize: `${NAV_RAIL_TARGET_SIZE}px`,
  px: 0,
  position: 'relative',
  _hover: { opacity: 0.85 },
  _focusVisible: { outline: 'none' },
  sx: {
    '&[aria-expanded=true]::after, &:focus-visible::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      boxShadow:
        'inset 0 0 0 var(--chakra-borderWidths-interactive) var(--chakra-colors-border-focus)',
      pointerEvents: 'none',
    },
  },
} as const;

/** The phone page title: a logo and chevron on a hover chip. */
const PHONE_TRIGGER_STYLE = {
  h: '44px',
  minW: '44px',
  px: 1,
  _hover: { bg: 'sidebar.item.hover' },
  _focusVisible: { boxShadow: 'outline', outline: 'none' },
} as const;

/** Past this many other workspaces, the switch list leads with a search. */
export const SWITCH_SEARCH_MIN = 8;

interface WorkspaceMenuProps {
  /**
   * `rail`: the desktop rail's tile. `mobile`: the phone Workspace page's
   * title, where there is no rail, so switching starts there instead.
   */
  variant?: 'rail' | 'mobile';
}

/**
 * The top of the nav rail: the current
 * workspace's logo (or monogram), the same square and radius as the Create
 * button under it, opening the workspace menu. Its name is in the menu's
 * header and the trigger's accessible name; the 88px rail has no room to
 * print it legibly.
 *
 * Everything about the WORKSPACE lives here (its settings, members, the
 * visualizer, switching); everything about YOU lives in `AccountMenu` at the
 * bottom of the rail. A long list of workspaces scrolls, and past
 * `SWITCH_SEARCH_MIN` it opens on a search box.
 *
 * Switching uses `useWorkspaceSwitcher`, like every other switcher, so the
 * unsaved-work guard and the double-switch protection come with it. The list
 * loads on open, not on mount: the rail renders on every page, and fetching on
 * mount would put a workspace-list request on every navigation.
 */
export function WorkspaceMenu({ variant = 'rail' }: WorkspaceMenuProps) {
  const isRail = variant === 'rail';
  const navigate = useNavigate();
  const isReadOnlyRole = useIsReadOnly();
  // Passive render-time check: the menu just hides the item, and the /graph
  // route runs its own gate.
  const { allowed: canUseVisualizer } = useCanUseFeature('knowledgeGraph', { track: false });
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const rowsRef = useRef<HTMLDivElement>(null);

  const {
    workspace,
    availableWorkspaces,
    loadWorkspaces,
    handleWorkspaceSwitch,
    pendingSwitch,
    confirmPendingSwitch,
    cancelPendingSwitch,
  } = useWorkspaceSwitcher();

  if (!workspace) return null;
  const others = availableWorkspaces.filter((ws) => ws.id !== workspace.id);
  const searchable = others.length >= SWITCH_SEARCH_MIN;
  const needle = query.trim().toLowerCase();
  const matches = (name: string) => !needle || name.toLowerCase().includes(needle);
  const matching = others.filter((ws) => matches(ws.name));
  const anyMatch = matching.length > 0;
  const memberCount = workspace.stats?.memberCount;

  return (
    <>
      <Menu
        isLazy
        placement={isRail ? 'right-start' : 'bottom-start'}
        // Chakra focuses its first item on open from a timeout, which can land
        // after the search box took focus (a slow device, every re-open). Its
        // initialFocusRef wins over that, so a long list opens on the search.
        initialFocusRef={searchable ? searchRef : undefined}
        onClose={() => setQuery('')}
        onOpen={() => {
          loadWorkspaces();
          safeAnalytics.track(ANALYTICS_EVENTS.WORKSPACE_MENU_OPENED, {
            source: isRail ? 'nav_rail' : 'mobile_workspace',
          });
        }}
      >
        <MenuButton
          aria-label={`Workspace: ${workspace.name}`}
          title={workspace.name}
          borderRadius="lg"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          transition="opacity 0.15s"
          {...(isRail ? RAIL_TRIGGER_STYLE : PHONE_TRIGGER_STYLE)}
          data-testid="rail-workspace-menu"
        >
          <HStack spacing={1}>
            <WorkspaceLogo workspace={workspace} size={isRail ? NAV_RAIL_TARGET_SIZE : 40} />
            {!isRail && <MaterialIcon name="expand_more" color="text.secondary" boxSize={5} />}
          </HStack>
        </MenuButton>
        <Portal>
          <MenuList
            zIndex="toast"
            minW="260px"
            maxW="min(320px, calc(100vw - 24px))"
            // The whole menu, not just its list, fits a short phone screen.
            maxH="calc(100dvh - 24px)"
            overflowY="auto"
            // Chakra focuses the list itself on open, after the search box
            // has taken focus whenever the list was already loaded (every open
            // after the first): hand that focus on to the search box.
            onFocus={(e) => {
              if (searchable && e.target === e.currentTarget) searchRef.current?.focus();
            }}
          >
            <HStack px={3} py={2} spacing={3}>
              <WorkspaceLogo workspace={workspace} size={40} />
              <VStack align="start" spacing={0} minW={0}>
                <Text fontWeight="semibold" color="text.primary" noOfLines={1}>
                  {workspace.name}
                </Text>
                {memberCount !== undefined && (
                  <Text fontSize="xs" color="text.secondary">
                    {memberCount === 1 ? '1 member' : `${memberCount} members`}
                  </Text>
                )}
              </VStack>
            </HStack>
            <MenuDivider />
            <MenuItem
              icon={<MaterialIcon name="settings" color="inherit" boxSize={MENU_ITEM_GLYPH_SIZE} />}
              onClick={() => navigate(`${ROUTES.settings}?tab=workspace`)}
            >
              Workspace settings
            </MenuItem>
            <MenuItem
              icon={<MaterialIcon name="group" color="inherit" boxSize={MENU_ITEM_GLYPH_SIZE} />}
              onClick={() => navigate(`${ROUTES.settings}?tab=members`)}
            >
              {isReadOnlyRole ? 'Members' : 'Members and invites'}
            </MenuItem>
            {/* Not on a phone: /graph is not a phone route. */}
            {isRail && canUseVisualizer && (
              <MenuItem
                icon={
                  <MaterialIcon name="neurology" color="inherit" boxSize={MENU_ITEM_GLYPH_SIZE} />
                }
                onClick={() => navigate(ROUTES.graph)}
              >
                Workspace visualizer
              </MenuItem>
            )}
            {others.length > 0 && (
              <>
                <MenuDivider />
                <MenuGroup title="Switch to">
                  {searchable && (
                    <SwitchSearch
                      inputRef={searchRef}
                      value={query}
                      onChange={setQuery}
                      onArrowDown={() =>
                        rowsRef.current
                          ?.querySelector<HTMLElement>('[role="menuitem"]:not([disabled])')
                          ?.focus()
                      }
                      onEnter={
                        matching.length === 1 ? () => handleWorkspaceSwitch(matching[0]) : undefined
                      }
                    />
                  )}
                  {/* The global focus ring is an outer shadow, which this
                      scroll box clips to a stray edge; draw it inside. */}
                  <Box
                    ref={rowsRef}
                    maxH="min(50vh, 360px)"
                    overflowY="auto"
                    sx={{
                      '& [role="menuitem"]:focus-visible': {
                        boxShadow:
                          'inset 0 0 0 var(--chakra-borderWidths-interactive) var(--chakra-colors-border-focus)',
                      },
                    }}
                  >
                    {/* Filtered rows are hidden, not unmounted: a Chakra item
                        that mounts moves focus to the menu list, which would
                        pull it out of the search box on every keystroke. */}
                    {others.map((ws) => (
                      <MenuItem
                        key={ws.id}
                        icon={<WorkspaceLogo workspace={ws} size={24} />}
                        onClick={() => handleWorkspaceSwitch(ws)}
                        isDisabled={!matches(ws.name)}
                        display={matches(ws.name) ? undefined : 'none'}
                      >
                        <Text as="span" noOfLines={1}>
                          {ws.name}
                        </Text>
                      </MenuItem>
                    ))}
                    {!anyMatch && (
                      <Text px={3} py={2} fontSize="sm" color="text.secondary">
                        No workspace matches.
                      </Text>
                    )}
                  </Box>
                </MenuGroup>
              </>
            )}
            <MenuDivider />
            {/* Self-serve creation is paused (`WorkspaceSelector`). */}
            <Text px={3} py={1} fontSize="xs" color="text.secondary">
              Need another workspace? Contact support.
            </Text>
          </MenuList>
        </Portal>
      </Menu>
      <UnsavedWorkDialog
        surface="workspace_switch"
        isOpen={!!pendingSwitch}
        onClose={cancelPendingSwitch}
        onConfirm={confirmPendingSwitch}
      />
    </>
  );
}

/**
 * A long switch list's search box. It takes focus when it appears (on a first
 * open the list loads after the menu has placed its focus; on later opens the
 * MenuList hands its own focus on to it) and clears the menu's focused item,
 * which Chakra would otherwise focus again on every render.
 */
function SwitchSearch({
  inputRef,
  value,
  onChange,
  onArrowDown,
  onEnter,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  value: string;
  onChange: (next: string) => void;
  /** Move into the list: its first matching row. */
  onArrowDown: () => void;
  /** Pick the one match; absent while more or fewer than one row matches. */
  onEnter?: () => void;
}) {
  const { setFocusedIndex, onClose } = useMenuContext();
  useEffect(() => {
    setFocusedIndex(-1);
    // A frame later: the rows mounting beside it move focus to the list.
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    // And after Chakra's own "focus the first item" timeout. On a first open
    // it was scheduled before the list loaded (so without initialFocusRef);
    // equal delays run in order, so this one, scheduled now, runs after it.
    const timeout = setTimeout(() => inputRef.current?.focus());
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timeout);
    };
  }, [setFocusedIndex, inputRef]);

  return (
    <Box px={3} pb={2}>
      <InputGroup size="sm">
        <InputLeftElement pointerEvents="none">
          <MaterialIcon name="search" boxSize={4} />
        </InputLeftElement>
        <Input
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocusedIndex(-1)}
          // The menu's own keys (typeahead, Home/End, arrows from the top of
          // the menu) are replaced here; Tab and Escape still reach it.
          onKeyDown={(e) => {
            if (e.key === 'Escape' || e.key === 'Tab') return;
            e.stopPropagation();
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              onArrowDown();
            } else if (e.key === 'Enter' && onEnter) {
              e.preventDefault();
              onClose();
              onEnter();
            }
          }}
          placeholder="Find a workspace"
          aria-label="Find a workspace"
          borderRadius="md"
        />
      </InputGroup>
    </Box>
  );
}
